import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type AdminRole = 'super_admin' | 'admin' | 'viewer';

export interface ImpersonationState {
  active: boolean;
  clientId: string | null;
  clientName: string | null;
  sessionId: string | null;
}

const EMPTY_IMPERSONATION: ImpersonationState = {
  active: false,
  clientId: null,
  clientName: null,
  sessionId: null,
};

interface AuthState {
  user: User | null;
  adminRole: AdminRole | null;
  clientId: string | null;
  isAdmin: boolean;
  isCustomer: boolean;
  isAffiliate: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null; adminRole: AdminRole | null; clientId: string | null; isAffiliate: boolean }>;
  signUp: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshIdentity: () => Promise<void>;
  impersonation: ImpersonationState;
  startImpersonation: (clientId: string, clientName: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface ResolvedIdentity {
  adminRole: AdminRole | null;
  clientId: string | null;
  isAffiliate: boolean;
}

async function resolveIdentity(user: User): Promise<ResolvedIdentity> {
  const role = user.app_metadata?.role as string | undefined;

  // ── Fast path: role stamped in JWT app_metadata (all new users) ──────────────
  if (role === 'affiliate') {
    return { adminRole: null, clientId: null, isAffiliate: true };
  }

  if (role === 'admin') {
    // Still need sub-role (super_admin / admin / viewer) from admin_profiles
    const { data } = await supabase
      .from('admin_profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();
    return { adminRole: (data?.role as AdminRole) ?? 'admin', clientId: null, isAffiliate: false };
  }

  if (role === 'customer') {
    const { data } = await supabase
      .from('clients')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();
    return { adminRole: null, clientId: data?.id ?? null, isAffiliate: false };
  }

  // ── Slow path: legacy users without app_metadata.role ────────────────────────
  // Run both table checks in parallel — one round-trip instead of two sequential.
  const [adminResult, clientResult, affiliateResult] = await Promise.allSettled([
    supabase.from('admin_profiles').select('role').eq('id', user.id).maybeSingle(),
    supabase.from('clients').select('id').eq('user_id', user.id).maybeSingle(),
    supabase.from('affiliates').select('id').eq('id', user.id).maybeSingle(),
  ]);

  if (adminResult.status === 'fulfilled' && !adminResult.value.error && adminResult.value.data) {
    return { adminRole: adminResult.value.data.role as AdminRole, clientId: null, isAffiliate: false };
  }

  if (clientResult.status === 'fulfilled' && !clientResult.value.error && clientResult.value.data) {
    return { adminRole: null, clientId: clientResult.value.data.id, isAffiliate: false };
  }

  if (affiliateResult.status === 'fulfilled' && !affiliateResult.value.error && affiliateResult.value.data) {
    return { adminRole: null, clientId: null, isAffiliate: true };
  }

  // Last-resort: email-based client link (handles SetPasswordPage edge case)
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data } = await supabase.functions.invoke('link-client-account', {
        body: {},
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (data?.client_id) {
        return { adminRole: null, clientId: data.client_id as string, isAffiliate: false };
      }
    }
  } catch {
    // fall through
  }

  return { adminRole: null, clientId: null, isAffiliate: false };
}

const EMPTY_STATE: AuthState = {
  user: null,
  adminRole: null,
  clientId: null,
  isAdmin: false,
  isCustomer: false,
  isAffiliate: false,
  isLoading: true,
};

function buildState(user: User, identity: ResolvedIdentity): AuthState {
  return {
    user,
    adminRole: identity.adminRole,
    clientId: identity.clientId,
    isAdmin: identity.adminRole !== null,
    isCustomer: identity.clientId !== null,
    isAffiliate: identity.isAffiliate,
    isLoading: false,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(EMPTY_STATE);

  const [impersonation, setImpersonation] = useState<ImpersonationState>(() => {
    try {
      const stored = sessionStorage.getItem('pm_impersonation');
      if (stored) return JSON.parse(stored) as ImpersonationState;
    } catch { /* ignore */ }
    return EMPTY_IMPERSONATION;
  });

  const impersonationRef = useRef<ImpersonationState>(impersonation);
  useEffect(() => { impersonationRef.current = impersonation; }, [impersonation]);

  const sessionTokenRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;

    const timeout = setTimeout(() => {
      if (active) setState(s => s.isLoading ? { ...s, isLoading: false } : s);
    }, 8_000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (active) sessionTokenRef.current = session?.access_token ?? null;
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;

      // Keep token ref current for synchronous beforeunload use
      if (session) sessionTokenRef.current = session.access_token;
      else sessionTokenRef.current = null;

      if (!session) {
        setState({ ...EMPTY_STATE, isLoading: false });
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        setState(prev => ({ ...prev, user: session.user }));
        return;
      }

      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        void (async () => {
          try {
            const identity = await resolveIdentity(session.user);
            if (active) {
              setState(prev => {
                // signIn/signUp may have already resolved identity — don't overwrite
                if (
                  prev.user?.id === session.user.id &&
                  (prev.clientId !== null || prev.adminRole !== null || prev.isAffiliate)
                ) {
                  return { ...prev, isLoading: false };
                }
                return buildState(session.user, identity);
              });
            }
          } catch {
            if (active) setState(s => ({ ...s, isLoading: false }));
          }
        })();
      }
    });

    const handleUnload = () => {
      const imp = impersonationRef.current;
      if (!imp.active || !imp.sessionId) return;
      const token = sessionTokenRef.current;
      if (!token) return;
      fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/end-impersonation`,
        {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: imp.sessionId }),
        },
      ).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      active = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const signIn = async (
    email: string,
    password: string
  ): Promise<{ error: string | null; adminRole: AdminRole | null; clientId: string | null; isAffiliate: boolean }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message, adminRole: null, clientId: null, isAffiliate: false };

      const identity = await resolveIdentity(data.user);
      setState(buildState(data.user, identity));
      return { error: null, adminRole: identity.adminRole, clientId: identity.clientId, isAffiliate: identity.isAffiliate };
    } catch {
      return { error: 'Connection error. Please refresh and try again.', adminRole: null, clientId: null, isAffiliate: false };
    }
  };

  const signUp = async (
    email: string,
    password: string
  ): Promise<{ error: string | null }> => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) return { error: error.message };
      if (!data.session) return { error: 'Sign up failed. Please try again.' };

      // Stamp customer role into app_metadata server-side (fire-and-forget — non-blocking).
      // Role will be in JWT on next token refresh. resolveIdentity falls back to table
      // lookup on the first session before the refresh happens.
      supabase.functions.invoke('set-user-role', {
        headers: { Authorization: `Bearer ${data.session.access_token}` },
      }).catch(() => {});

      const identity = await resolveIdentity(data.user!);
      setState(buildState(data.user!, identity));
      return { error: null };
    } catch {
      return { error: 'Connection error. Please refresh and try again.' };
    }
  };

  const refreshIdentity = useCallback(async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const identity = await resolveIdentity(user);
    setState(prev => ({ ...prev, ...buildState(user, identity) }));
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setState({ ...EMPTY_STATE, isLoading: false });
  };

  const startImpersonation = async (clientId: string, clientName: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-impersonation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ client_id: clientId }),
      },
    );
    const json = await res.json() as { session_id?: string; client_name?: string; error?: string };
    if (!res.ok || !json.session_id) {
      throw new Error(json.error ?? 'Failed to start impersonation');
    }
    const next: ImpersonationState = {
      active: true,
      clientId,
      clientName: json.client_name ?? clientName,
      sessionId: json.session_id,
    };
    setImpersonation(next);
    try { sessionStorage.setItem('pm_impersonation', JSON.stringify(next)); } catch { /* ignore */ }
  };

  const stopImpersonation = async (): Promise<void> => {
    const sessionId = impersonation.sessionId;
    setImpersonation(EMPTY_IMPERSONATION);
    try { sessionStorage.removeItem('pm_impersonation'); } catch { /* ignore */ }
    if (!sessionId) return;
    const { data: { session } } = await supabase.auth.getSession();
    fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/end-impersonation`,
      {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      },
    ).catch(() => {});
  };

  const contextValue: AuthContextValue = {
    ...state,
    clientId: impersonation.active ? impersonation.clientId : state.clientId,
    isCustomer: impersonation.active ? true : state.isCustomer,
    isAdmin: impersonation.active ? false : state.isAdmin,
    signIn,
    signUp,
    signOut,
    refreshIdentity,
    impersonation,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
